import { useQuery } from '@tanstack/react-query';

import { BENTOBOX_BOOKING_ENDPOINT } from 'reservations/base/constants/endpoints';
import { HTTP_STATUS_CODE_403_UNAUTHORIZED } from 'reservations/base/constants/httpStatusCodes';
import {
  FORCE_REDIRECT_ARGS,
  REDIRECT_WITH_ACCOUNT_DETAILS
} from 'reservations/base/constants/redirects';
import { MINUTE_IN_MILLISECONDS } from 'reservations/base/constants/time';
import { buildBentoboxApiEndpoint } from 'reservations/base/helpers/buildBentoboxApiEndpoint';
import is5xxError from 'reservations/base/helpers/is5xxError';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import { BookingResponse } from 'reservations/booking/types';
import { restClient } from 'utility/restClient';
import type { RequestError } from 'utility/restClient';

const DEFAULT_QUERY_OPTIONS = {
  staleTime: MINUTE_IN_MILLISECONDS * 5,
  gcTime: MINUTE_IN_MILLISECONDS * 5,
  refetchInterval: MINUTE_IN_MILLISECONDS * 5
};

async function fetchBooking(accessToken: string, bookingId: string) {
  const endpoint = buildBentoboxApiEndpoint(BENTOBOX_BOOKING_ENDPOINT, {
    access_token: encodeURIComponent(accessToken),
    booking_id: bookingId
  });
  const response = await restClient.get(endpoint);

  try {
    return BookingResponse.parse(response);
  } catch (err) {
    console.error(`fetchBooking: ${err}`);
    throw err;
  }
}

export const useBookingQuery = (accessToken: string, bookingId: string) => {
  const { handleNavigation } = useCustomRouter();

  return useQuery({
    ...DEFAULT_QUERY_OPTIONS,
    queryKey: ['bentoboxReservations', 'booking', bookingId],
    queryFn: () => fetchBooking(accessToken, bookingId),
    enabled: true,
    retry: false,
    meta: {
      onQueryError: (error: RequestError) => {
        if (is5xxError(error.code)) {
          handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
          return;
        }

        // istanbul ignore next
        if (error.code === HTTP_STATUS_CODE_403_UNAUTHORIZED) {
          handleNavigation(...FORCE_REDIRECT_ARGS);
          return;
        }
      }
    }
  });
};
